import { useEffect, useState } from 'react';
import type { UserData } from '../firebase/firebase.types';
import { getUserDocumentsWithIds } from '../firebase/users.utils';

// Simple cache to prevent repeated fetches
const userCache = new Map<string, UserData>();

/** The `users/{uid}` document for a user, e.g. a card's creator. Cached for the session. */
function useUserDetails(userId?: string) {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) {
      setUserData(null);
      return;
    }

    const cachedData = userCache.get(userId);
    if (cachedData) {
      setUserData(cachedData);
      return;
    }

    let isSubscribed = true;
    setLoading(true);
    getUserDocumentsWithIds([userId])
      .then((userDocs) => {
        if (!isSubscribed) {
          return;
        }
        if (userDocs.empty) {
          setUserData(null);
          return;
        }
        const data = userDocs.docs[0].data() as UserData;
        userCache.set(userId, data);
        setUserData(data);
      })
      .catch(() => {
        if (isSubscribed) {
          setUserData(null);
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [userId]);

  return { userData, loading };
}

export default useUserDetails;
